import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Search, Filter, MapPin, ArrowUpDown, ChevronUp, ChevronDown, Download, Package, AlertTriangle } from 'lucide-react';
import { io } from 'socket.io-client';
import { useAuth } from '../AuthContext';
import { useSortableData } from '../hooks/useSortableData';
import Pagination from './Pagination';
const BinLocations = () => {
    const { user } = useAuth();
    const isWarehouseRestricted = user && (user.role === 'manager' || user.role === 'picker') && user.warehouse;
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [locationSearch, setLocationSearch] = useState('');
    const [selectedClient, setSelectedClient] = useState('All');
    const [selectedWarehouse, setSelectedWarehouse] = useState(isWarehouseRestricted ? user.warehouse : 'All');
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedType, setSelectedType] = useState('All');
    const [uniqueClients, setUniqueClients] = useState(['All']);
    const [uniqueTypes, setUniqueTypes] = useState(['All']);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [stats, setStats] = useState({ global: 0, warehouse: 0, stocked: 0, empty: 0, breakdown: [] });
    const itemsPerPage = 25;

    const { items: sortedLocations, requestSort, sortConfig } = useSortableData(locations);
    const getSortIcon = (key) => {
        if (!sortConfig || sortConfig.key !== key) return <ArrowUpDown size={14} style={{ display: 'inline', marginLeft: '4px', opacity: 0.3 }} />;
        return sortConfig.direction === 'ascending' ? <ChevronUp size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} /> : <ChevronDown size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--primary)' }} />;
    };

    // Hardcode unique warehouses for the dropdown to avoid heavy lookup queries
    const uniqueWarehouses = ['All', 'Primary', 'SC_Ramona', 'SC_DALLAS', 'SC_FRESNO', 'SC_Dubai', 'DRMTLGY', 'KITSCH_LA'];

    useEffect(() => {
        const fetchLocations = async () => {
            setLoading(true);
            try {
                const res = await api.inventory.getLocations(currentPage, itemsPerPage, searchTerm, selectedWarehouse, locationSearch, selectedClient, selectedStatus, selectedType).catch(() => null);
                if (res?.data) {
                    setLocations(res.data.data || []);
                    setTotalPages(res.data.totalPages || 0);
                    setTotalRecords(res.data.totalRecords || 0);
                    setStats({
                        global: res.data.totalBinsGlobal || 0,
                        warehouse: res.data.totalBinsWarehouse || 0,
                        stocked: res.data.stockedBinsWarehouse || 0,
                        empty: res.data.emptyBinsWarehouse || 0,
                        breakdown: res.data.warehouseBreakdown || []
                    });
                    if (res.data.uniqueClients) setUniqueClients(res.data.uniqueClients);
                    if (res.data.uniqueTypes) setUniqueTypes(res.data.uniqueTypes);
                } else {
                    setLocations([]);
                }
            } catch (error) {
                console.error("Bin Locations Error", error);
            } finally {
                setLoading(false);
            }
        };

        const debounce = setTimeout(() => {
            fetchLocations();
        }, 300);

        return () => clearTimeout(debounce);
    }, [currentPage, searchTerm, selectedClient, selectedWarehouse, locationSearch, selectedStatus, selectedType]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, selectedClient, selectedWarehouse, locationSearch, selectedStatus, selectedType]);

    // Real-time updates via Socket.io
    useEffect(() => {
        const socketUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8009/api').replace('/api', '');
        const socket = io(socketUrl);

        socket.on('bin_inventory_updated', (data) => {
            console.log("Real-time bin update received:", data);
            // Refresh the current page of locations
            // We re-fetch to ensure consistency with pagination and filters
            const fetchLocations = async () => {
                try {
                    const res = await api.inventory.getLocations(currentPage, itemsPerPage, searchTerm, selectedWarehouse, locationSearch, selectedClient, selectedStatus, selectedType).catch(() => null);
                    if (res?.data) {
                        setLocations(res.data.data || []);
                        setTotalPages(res.data.totalPages || 0);
                        setTotalRecords(res.data.totalRecords || 0);
                        setStats({
                            global: res.data.totalBinsGlobal || 0,
                            warehouse: res.data.totalBinsWarehouse || 0,
                            stocked: res.data.stockedBinsWarehouse || 0,
                            empty: res.data.emptyBinsWarehouse || 0,
                            breakdown: res.data.warehouseBreakdown || []
                        });
                    }
                } catch (error) {
                    console.error("Bin Locations Refresh Error", error);
                }
            };
            fetchLocations();
        });

        return () => {
            socket.disconnect();
        };
    }, [currentPage, searchTerm, selectedClient, selectedWarehouse, locationSearch, selectedStatus, selectedType]);

    return (
        <div>
            <div className="page-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h1>Warehouse Bin Locations</h1>
                    <p>Track precise inventory quantities at the location-bin level.</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div className="card stat-card" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', borderLeft: '4px solid var(--primary)' }}>
                    <div style={{ padding: '0.75rem', borderRadius: '12px', backgroundColor: 'rgba(79, 70, 229, 0.1)', color: 'var(--primary)' }}>
                        <MapPin size={24} />
                    </div>
                    <div>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.25rem', fontWeight: 500 }}>Total Unique Bins</p>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{stats.global.toLocaleString()}</h2>
                    </div>
                </div>

                <div className="card stat-card" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', borderLeft: '4px solid #10b981' }}>
                    <div style={{ padding: '0.75rem', borderRadius: '12px', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                        <Filter size={24} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.25rem', fontWeight: 500 }}>
                            {selectedWarehouse === 'All' ? 'Bins Per Warehouse' : `Bins in ${selectedWarehouse}`}
                        </p>
                        {selectedWarehouse === 'All' ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                                {stats.breakdown.length > 0 ? stats.breakdown.map((wh, idx) => (
                                    <div key={wh.name} style={{
                                        fontSize: '0.75rem',
                                        padding: '0.2rem 0.6rem',
                                        backgroundColor: '#f1f5f9',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <span style={{ fontWeight: 600 }}>{wh.name}:</span>
                                        <span style={{ color: '#059669', fontWeight: 700 }}>{wh.count}</span>
                                    </div>
                                )) : (
                                    <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Calculating...</span>
                                )}
                            </div>
                        ) : (
                            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
                                {stats.warehouse.toLocaleString()}
                            </h2>
                        )}
                    </div>
                </div>

                <div className="card stat-card" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', borderLeft: '4px solid #10b981' }}>
                    <div style={{ padding: '0.75rem', borderRadius: '12px', backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
                        <Package size={24} />
                    </div>
                    <div>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.25rem', fontWeight: 500 }}>
                            {selectedWarehouse === 'All' ? 'Stocked Bins Globally' : `Stocked in ${selectedWarehouse}`}
                        </p>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{stats.stocked.toLocaleString()}</h2>
                    </div>
                </div>

                <div className="card stat-card" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', borderLeft: '4px solid var(--danger)' }}>
                    <div style={{ padding: '0.75rem', borderRadius: '12px', backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)' }}>
                        <AlertTriangle size={24} />
                    </div>
                    <div>
                        <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '0.25rem', fontWeight: 500 }}>
                            {selectedWarehouse === 'All' ? 'Empty Bins Globally' : `Empty in ${selectedWarehouse}`}
                        </p>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{stats.empty.toLocaleString()}</h2>
                    </div>
                </div>
            </div>

            <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem 1.5rem' }}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
                        <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            placeholder="SKU or Product Name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%', padding: '0.6rem 1rem 0.6rem 2.5rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit'
                            }}
                        />
                    </div>

                    <div style={{ flex: '1', minWidth: '250px', position: 'relative' }}>
                        <MapPin size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            placeholder="Exact Bin / Location Name..."
                            value={locationSearch}
                            onChange={(e) => setLocationSearch(e.target.value)}
                            style={{
                                width: '100%', padding: '0.6rem 1rem 0.6rem 2.5rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit'
                            }}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '450px' }}>
                        <Filter size={18} color="var(--text-muted)" />
                        <select
                            value={selectedClient}
                            onChange={(e) => setSelectedClient(e.target.value)}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: 'white'
                            }}
                        >
                            {uniqueClients.map(c => (
                                <option key={c} value={c}>{c === 'All' ? 'All Clients' : c}</option>
                            ))}
                        </select>
                        <select
                            value={selectedWarehouse}
                            onChange={(e) => !isWarehouseRestricted && setSelectedWarehouse(e.target.value)}
                            disabled={isWarehouseRestricted}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: isWarehouseRestricted ? '#f1f5f9' : 'white',
                                opacity: isWarehouseRestricted ? 0.8 : 1
                            }}
                        >
                            {isWarehouseRestricted ? (
                                <option value={user.warehouse}>{user.warehouse}</option>
                            ) : (
                                uniqueWarehouses.map(wh => (
                                    <option key={wh} value={wh}>{wh === 'All' ? 'All Warehouses' : wh}</option>
                                ))
                            )}
                        </select>
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: 'white'
                            }}
                        >
                            <option value="Stocked">Stocked</option>
                            <option value="Empty">Empty</option>
                        </select>
                        <select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                            style={{
                                width: '100%', padding: '0.6rem 1rem',
                                borderRadius: '6px', border: '1px solid #e2e8f0',
                                outline: 'none', fontFamily: 'inherit',
                                backgroundColor: 'white'
                            }}
                        >
                            {uniqueTypes.map(t => (
                                <option key={t} value={t}>{t === 'All' ? 'All Types' : t}</option>
                            ))}
                        </select>
                        <button
                            onClick={() => window.location.href = api.inventory.exportLocationsUrl(searchTerm, selectedWarehouse, locationSearch, selectedClient, selectedStatus, selectedType)}
                            className="settings-add-btn"
                            style={{ marginLeft: '0.5rem', height: '100%', borderRadius: '6px', padding: '0 1.25rem' }}
                        >
                            <Download size={18} />
                            Export
                        </button>
                    </div>
                </div>
            </div>

            <div className="card table-container">
                {loading ? (
                    <p>Loading bin locations...</p>
                ) : (
                    <table className="responsive-table">
                        <thead>
                            <tr>
                                <th className="sortable" onClick={() => requestSort('warehouse_name')}>Warehouse {getSortIcon('warehouse_name')}</th>
                                <th className="sortable" onClick={() => requestSort('clientName')}>Client {getSortIcon('clientName')}</th>
                                <th className="sortable" onClick={() => requestSort('location_name')}>Bin / Location {getSortIcon('location_name')}</th>
                                <th className="sortable" onClick={() => requestSort('location_type')}>Type {getSortIcon('location_type')}</th>
                                <th className="sortable" onClick={() => requestSort('sku')}>SKU {getSortIcon('sku')}</th>
                                <th className="sortable" onClick={() => requestSort('product_name')}>Product Name {getSortIcon('product_name')}</th>
                                <th className="sortable" onClick={() => requestSort('quantity')}>Quantity {getSortIcon('quantity')}</th>
                                <th className="sortable" onClick={() => requestSort('updated_at')}>Last Updated {getSortIcon('updated_at')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {locations.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--text-muted)' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                                            <MapPin size={48} style={{ opacity: 0.2 }} />
                                            <div>
                                                <p style={{ fontWeight: 500, color: 'var(--text-main)', marginBottom: '0.25rem' }}>No bin locations found</p>
                                                <p style={{ fontSize: '0.875rem' }}>Try adjusting your search criteria, or wait for new webhook data.</p>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                sortedLocations.map((loc, idx) => (
                                    <tr key={`${loc.sku}-${loc.location_name}-${idx}`}>
                                        <td data-label="Warehouse" title={loc.warehouse_name || 'N/A'}>{loc.warehouse_name || 'N/A'}</td>
                                        <td data-label="Client" style={{ fontWeight: 600, color: 'var(--primary)' }} title={loc.clientName}>{loc.clientName}</td>
                                        <td data-label="Bin / Location" style={{ fontWeight: 500, color: 'var(--text-main)' }} title={loc.location_name}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <MapPin size={14} color="var(--primary)" />
                                                {loc.location_name}
                                            </div>
                                        </td>
                                        <td data-label="Type" title={loc.location_type}>{loc.location_type}</td>
                                        <td data-label="SKU" style={{ fontWeight: 600, color: 'var(--text-main)' }} title={loc.sku}>{loc.sku}</td>
                                        <td data-label="Product Name" title={loc.product_name}>{loc.product_name}</td>
                                        <td data-label="Quantity">
                                            <span style={{
                                                backgroundColor: parseInt(loc.quantity || 0) > 0 ? '#dcfce7' : '#fee2e2',
                                                color: parseInt(loc.quantity || 0) > 0 ? '#166534' : '#991b1b',
                                                padding: '0.25rem 0.75rem',
                                                borderRadius: '6px',
                                                fontWeight: 'bold',
                                                display: 'inline-block'
                                            }}>
                                                {loc.quantity}
                                            </span>
                                        </td>
                                        <td data-label="Last Updated" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                                            {loc.updated_at ? new Date(loc.updated_at).toLocaleString() : 'N/A'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}

                {!loading && totalRecords > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 8px 8px' }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalRecords)} of {totalRecords} entries
                        </div>
                            <Pagination currentPage={currentPage} totalPages={totalPages} setCurrentPage={setCurrentPage} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default BinLocations;
